/**
 * A viewer's landing on app.cpg.computer: the screens brands have shared with them, each a link into that brand's
 * own app, where they sign in once more (the same account) and see only those screens.
 */
import { accessWords, sharedCount } from '../../domain/sharing'
import type { Api } from '../../infra/api'
import { Button, Card, DataTable, EmptyState, Screen, Stack, Text } from '../../ui'
import { useLoad } from '../useLoad'
import { useNow } from '../useNow'

export function SharedWithMe({ api, email, onSignOut }: { api: Api; email: string; onSignOut: () => void }) {
  const query = useLoad(() => api.sharedWithMe(), [api])
  const now = useNow(60_000)
  return (
    <Screen
      title="Shared with you"
      count={query.data ? sharedCount(query.data) : undefined}
      description={`Signed in as ${email}. Each screen opens in that brand's own app, where you see only what they shared.`}
      actions={<Button variant="ghost" size="sm" onClick={onSignOut}>Sign out</Button>}
      query={query}
      skeleton="cards"
    >
      {(d) =>
        d.apps.length === 0 ? (
          <EmptyState title="Nothing shared with you yet.">When a brand shares a screen with {email}, it shows up here.</EmptyState>
        ) : (
          <Stack gap="lg">
            {d.apps.map((a) => (
              <Card key={a.founder.id} title={a.founder.name} count={a.screens.length} flush>
                <DataTable
                  label={`Screens ${a.founder.name} shared`}
                  framed={false}
                  rows={a.screens}
                  rowKey={(s) => s.path}
                  empty={<Text tone="muted">Nothing here now.</Text>}
                  columns={[
                    {
                      key: 'screen',
                      label: 'Screen',
                      narrow: 'main',
                      render: (s) => (
                        <Stack gap="none">
                          <Text weight="head">
                            {s.url ? (
                              <a href={s.url} target="_blank" rel="noreferrer">
                                {s.title}
                              </a>
                            ) : (
                              s.title
                            )}
                          </Text>
                          {s.description && <Text size="sm" tone="muted">{s.description}</Text>}
                        </Stack>
                      ),
                    },
                    { key: 'ends', label: 'Access', narrow: 'below', render: (s) => <Text size="sm" tone="muted">{accessWords(s.expiresAt, now)}</Text> },
                    {
                      key: 'open',
                      label: 'Open',
                      align: 'end',
                      narrow: 'end',
                      narrowLabel: false,
                      render: (s) =>
                        s.url ? (
                          <Text size="sm">
                            <a href={s.url} target="_blank" rel="noreferrer">
                              Open
                            </a>
                          </Text>
                        ) : (
                          <Text size="sm" tone="muted">Not live yet</Text>
                        ),
                    },
                  ]}
                />
              </Card>
            ))}
          </Stack>
        )
      }
    </Screen>
  )
}
